(function () {
  'use strict';

  if (window.MrMapleSearchIntelligence) return;

  var SEARCH_PAGE_CLASS = 'template-search';
  var MIN_QUERY_LENGTH = 4;
  var indexPromise = null;
  var suggestionCache = new Map();

  function cleanTitle(value) {
    return String(value || '')
      .replace(/^(?:FOR\s+)?PICK\s*UP\s+ONLY\s*\|\s*/gi, '')
      .replace(/\s*\|\s*DOES NOT SHIP\s*$/gi, '')
      .replace(/DOES NOT SHIP\s*$/gi, '')
      .trim();
  }

  function normalizeText(value) {
    return cleanTitle(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’‘]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/×/g, ' x ')
      .replace(/&/g, ' and ')
      .replace(/[^a-zA-Z0-9'"\s-]+/g, ' ')
      .replace(/['"]/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function getTokens(value) {
    return normalizeText(value)
      .split(' ')
      .filter(function (token) {
        return token && token.length >= 2;
      });
  }

  function getSpellIndexUrl() {
    return (
      (window.Theme && window.Theme.routes && window.Theme.routes.search_spell_index_url) ||
      window.MRMAPLE_SEARCH_SPELL_INDEX_URL ||
      ''
    );
  }

  function getSearchUrl(query) {
    var baseUrl = (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search';
    return baseUrl + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;

    var spellIndexUrl = getSpellIndexUrl();
    if (!spellIndexUrl) {
      indexPromise = Promise.resolve({ e: [] });
      return indexPromise;
    }

    indexPromise = fetch(spellIndexUrl, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Search spell index unavailable');
        }

        return response.json();
      })
      .catch(function () {
        return { e: [] };
      });

    return indexPromise;
  }

  function boundedLevenshtein(left, right, maxDistance) {
    if (left === right) return 0;
    if (!left || !right) return Math.max(left.length, right.length);
    if (Math.abs(left.length - right.length) > maxDistance) return maxDistance + 1;

    var previous = new Array(right.length + 1);
    var current = new Array(right.length + 1);
    var index;

    for (index = 0; index <= right.length; index += 1) {
      previous[index] = index;
    }

    for (var leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
      current[0] = leftIndex;
      var rowMin = current[0];

      for (var rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
        var substitutionCost = left.charAt(leftIndex - 1) === right.charAt(rightIndex - 1) ? 0 : 1;
        current[rightIndex] = Math.min(
          previous[rightIndex] + 1,
          current[rightIndex - 1] + 1,
          previous[rightIndex - 1] + substitutionCost,
        );

        if (current[rightIndex] < rowMin) {
          rowMin = current[rightIndex];
        }
      }

      if (rowMin > maxDistance) return maxDistance + 1;

      for (index = 0; index <= right.length; index += 1) {
        previous[index] = current[index];
      }
    }

    return previous[right.length];
  }

  function scoreToken(queryToken, candidateToken) {
    if (!queryToken || !candidateToken) return 0;
    if (queryToken === candidateToken) return 1;

    if (
      candidateToken.indexOf(queryToken) === 0 ||
      queryToken.indexOf(candidateToken) === 0
    ) {
      return 0.92 - Math.min(Math.abs(candidateToken.length - queryToken.length), 6) * 0.03;
    }

    var maxLength = Math.max(queryToken.length, candidateToken.length);
    var maxDistance = Math.max(2, Math.ceil(maxLength * 0.35));
    var distance = boundedLevenshtein(queryToken, candidateToken, maxDistance);

    if (distance > maxDistance) return 0;

    return Math.max(0, 1 - distance / maxLength);
  }

  function scorePhrase(queryNormalized, queryTokens, phraseNormalized) {
    if (!phraseNormalized) return 0;
    if (queryNormalized === phraseNormalized) return 1;

    var phraseTokens = phraseNormalized.split(' ').filter(Boolean);
    if (!queryTokens.length || !phraseTokens.length) return 0;

    var totalScore = 0;
    var matchedTokens = 0;

    queryTokens.forEach(function (queryToken) {
      var bestTokenScore = 0;

      phraseTokens.forEach(function (candidateToken) {
        var tokenScore = scoreToken(queryToken, candidateToken);
        if (tokenScore > bestTokenScore) {
          bestTokenScore = tokenScore;
        }
      });

      totalScore += bestTokenScore;
      if (bestTokenScore >= 0.72) {
        matchedTokens += 1;
      }
    });

    var averageScore = totalScore / queryTokens.length;
    var coverageScore = matchedTokens / queryTokens.length;
    var substringBonus = 0;

    if (
      phraseNormalized.indexOf(queryNormalized) !== -1 ||
      queryNormalized.indexOf(phraseNormalized) !== -1
    ) {
      substringBonus = 0.08;
    }

    return Math.min(1, averageScore * 0.75 + coverageScore * 0.25 + substringBonus);
  }

  function getCandidateFirstLetters(phrases) {
    var letters = new Set();

    (phrases || []).forEach(function (phrase) {
      phrase.split(' ').forEach(function (token) {
        if (token) {
          letters.add(token.charAt(0));
        }
      });
    });

    return letters;
  }

  function scoreEntry(queryNormalized, queryTokens, entry) {
    var bestScore = 0;

    (entry.p || []).forEach(function (phrase) {
      var phraseScore = scorePhrase(queryNormalized, queryTokens, phrase);
      if (phraseScore > bestScore) {
        bestScore = phraseScore;
      }
    });

    return bestScore;
  }

  function isSuggestionConfident(best, secondBest) {
    if (!best || best.score < 0.8) return false;
    if (!secondBest) return best.score >= 0.84;
    return best.score >= 0.84 || best.score - secondBest.score >= 0.08;
  }

  function hasProductMatches(root) {
    return Boolean(root && root.querySelector('.search-flydown--product-list-item'));
  }

  async function findSuggestion(query) {
    var normalizedQuery = normalizeText(query);
    if (!normalizedQuery || normalizedQuery.length < MIN_QUERY_LENGTH) return null;

    if (suggestionCache.has(normalizedQuery)) {
      return suggestionCache.get(normalizedQuery);
    }

    var queryTokens = getTokens(normalizedQuery);
    if (!queryTokens.length) return null;

    var queryFirstLetters = new Set(
      queryTokens.map(function (token) {
        return token.charAt(0);
      }),
    );

    var index = await loadIndex();
    var entries = Array.isArray(index.e) ? index.e : [];
    var candidates = entries.filter(function (entry) {
      var entryLetters = getCandidateFirstLetters(entry.p);

      if (!entryLetters.size) return false;

      for (var letter of queryFirstLetters) {
        if (entryLetters.has(letter)) {
          return true;
        }
      }

      return false;
    });

    if (!candidates.length) {
      candidates = entries;
    }

    var best = null;
    var secondBest = null;

    candidates.forEach(function (entry) {
      var score = scoreEntry(normalizedQuery, queryTokens, entry);

      if (!best || score > best.score) {
        secondBest = best;
        best = { entry: entry, score: score };
        return;
      }

      if (!secondBest || score > secondBest.score) {
        secondBest = { entry: entry, score: score };
      }
    });

    var suggestion = null;

    if (best && normalizeText(best.entry.l) !== normalizedQuery && isSuggestionConfident(best, secondBest)) {
      suggestion = {
        query: best.entry.l,
        label: best.entry.l,
        score: best.score,
      };
    }

    suggestionCache.set(normalizedQuery, suggestion);
    return suggestion;
  }

  function getDatasetValue(scope, key) {
    if (!scope || !scope.dataset) return '';
    return String(scope.dataset[key] || '').trim();
  }

  function getPredictiveListRoot(scope) {
    if (!scope) return null;
    return scope.querySelector('.search-flydown--product-items .search-flydown__group-list');
  }

  function getPredictiveItemTitle(item, card) {
    return cleanTitle(getDatasetValue(card, 'productTitle')) ||
      cleanTitle(item.querySelector('.search-flydown--product-title') && item.querySelector('.search-flydown--product-title').textContent);
  }

  function getPredictiveItemKey(item) {
    if (!item) return '';

    var card = item.querySelector('[data-wishlist-card]');
    var productId = getDatasetValue(card, 'productId');
    if (productId) return 'id:' + productId;

    var handle = getDatasetValue(card, 'productHandle');
    if (handle) return 'handle:' + normalizeText(handle);

    var link = item.querySelector('.search-flydown--product[href]');
    var href = link && link.getAttribute('href');
    if (href) {
      try {
        return 'path:' + new URL(href, window.location.origin).pathname.replace(/\/+$/, '').toLowerCase();
      } catch (error) {
        return 'path:' + href.split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase();
      }
    }

    var title = getPredictiveItemTitle(item, card);
    return title ? 'title:' + normalizeText(title) : '';
  }

  function dedupePredictiveResults(scope) {
    var listRoot = getPredictiveListRoot(scope);
    if (!listRoot) return 0;

    var seen = new Set();
    var removed = 0;

    Array.from(listRoot.querySelectorAll('.search-flydown--product-list-item')).forEach(function (item) {
      var key = getPredictiveItemKey(item);
      if (!key) return;

      if (seen.has(key)) {
        item.remove();
        removed += 1;
        return;
      }

      seen.add(key);
    });

    return removed;
  }

  function rerankPredictiveResults(scope, query) {
    if (!scope || !query) return;

    var queryNormalized = normalizeText(query);
    var queryTokens = getTokens(queryNormalized);
    if (!queryTokens.length) return;

    dedupePredictiveResults(scope);

    var listRoot = getPredictiveListRoot(scope);
    if (!listRoot) return;

    var items = Array.from(listRoot.querySelectorAll('.search-flydown--product-list-item'));
    if (items.length < 2) return;

    var ranked = items
      .map(function (item, index) {
        var card = item.querySelector('[data-wishlist-card]');
        var title = getPredictiveItemTitle(item, card);
        var type = getDatasetValue(card, 'productType');
        var tags = getDatasetValue(card, 'productTags').replace(/,/g, ' ');
        var handle = getDatasetValue(card, 'productHandle').replace(/-/g, ' ');
        var score = scoreEntry(queryNormalized, queryTokens, {
          p: [
            normalizeText(title),
            normalizeText(type),
            normalizeText(tags),
            normalizeText(handle),
          ].filter(Boolean),
        });

        return {
          item: item,
          index: index,
          score: score,
        };
      })
      .sort(function (left, right) {
        if (right.score !== left.score) return right.score - left.score;
        return left.index - right.index;
      });

    ranked.forEach(function (entry) {
      listRoot.appendChild(entry.item);
    });
  }

  function clearPredictiveSuggestion(scope) {
    if (!scope) return;
    var existing = scope.querySelector('[data-mm-search-suggestion]');
    if (existing) existing.remove();
  }

  function simplifyEmptyPredictiveState(scope) {
    if (!scope) return;

    var noResults = scope.querySelector('.search-flydown__no-results');
    if (noResults) noResults.remove();

    var continueButton = scope.querySelector('.search-flydown__continue');
    if (continueButton) continueButton.remove();
  }

  function injectPredictiveSuggestion(scope, options) {
    if (!scope || !options || !options.correctedQuery) return;

    clearPredictiveSuggestion(scope);

    var isEmptyResults = Boolean(options.emptyResults);
    var displayQuery = String(options.originalQuery || options.correctedQuery || '').trim();

    if (isEmptyResults) {
      simplifyEmptyPredictiveState(scope);
    }

    var banner = document.createElement('div');
    banner.className = 'mm-search-suggestion' + (isEmptyResults ? ' mm-search-suggestion--empty' : '');
    banner.setAttribute('data-mm-search-suggestion', '');

    var text = document.createElement('span');
    text.className = 'mm-search-suggestion__text';

    if (isEmptyResults) {
      text.textContent = displayQuery
        ? 'No available products found for "' + displayQuery + '".'
        : 'No available products found.';
    } else {
      text.textContent = 'Showing closest matches for "' + options.correctedQuery + '".';
    }

    var link = document.createElement('a');
    link.className = 'mm-search-suggestion__link';
    link.href = getSearchUrl(isEmptyResults ? displayQuery : options.correctedQuery);
    link.textContent = isEmptyResults ? 'Search all results' : 'View search results';
    if (isEmptyResults && displayQuery) {
      link.setAttribute('aria-label', 'Search all results for "' + displayQuery + '"');
    }

    banner.appendChild(text);

    if (isEmptyResults && normalizeText(options.correctedQuery) !== normalizeText(displayQuery)) {
      var helper = document.createElement('span');
      helper.className = 'mm-search-suggestion__helper';
      helper.textContent = 'Closest catalog match: ' + options.correctedQuery + ', but it is not available right now.';
      banner.appendChild(helper);
    }

    banner.appendChild(link);

    if (!isEmptyResults && options.originalQuery) {
      var originalLink = document.createElement('a');
      originalLink.className = 'mm-search-suggestion__link mm-search-suggestion__link--muted';
      originalLink.href = getSearchUrl(options.originalQuery);
      originalLink.textContent = 'Search instead for "' + options.originalQuery + '"';
      banner.appendChild(originalLink);
    }

    scope.insertBefore(banner, scope.firstChild);
  }

  function injectSearchPageSuggestion(root, suggestion, originalQuery) {
    if (!root || !suggestion || !suggestion.query) return;
    if (root.querySelector('[data-mm-search-page-suggestion]')) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'mm-search-page-suggestion';
    wrapper.setAttribute('data-mm-search-page-suggestion', '');

    var title = document.createElement('p');
    title.className = 'mm-search-page-suggestion__title';
    title.textContent = 'Try this search instead';

    var link = document.createElement('a');
    link.className = 'mm-search-page-suggestion__link';
    link.href = getSearchUrl(suggestion.query);
    link.textContent = suggestion.query;

    wrapper.appendChild(title);
    wrapper.appendChild(link);

    if (originalQuery) {
      var helper = document.createElement('p');
      helper.className = 'mm-search-page-suggestion__helper';
      helper.textContent = 'We could not find exact matches for "' + originalQuery + '".';
      wrapper.insertBefore(helper, link);
    }

    root.appendChild(wrapper);
  }

  function normalizeLuckyUrl(value) {
    var rawValue = String(value || '').trim();
    if (!rawValue || rawValue.charAt(0) === '#') return '';

    try {
      var baseOrigin = window.location.origin && window.location.origin !== 'null'
        ? window.location.origin
        : 'https://mrmaple.local';
      var url = new URL(rawValue, baseOrigin);
      if (url.origin !== baseOrigin) return '';
      return url.pathname + url.search + url.hash;
    } catch (error) {
      return '';
    }
  }

  function getLuckyUrls(link) {
    if (!link) return [];

    var seen = new Set();
    var urls = String(link.getAttribute('data-search-lucky-urls') || '')
      .split('|')
      .map(normalizeLuckyUrl)
      .filter(function (url) {
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      });

    if (!urls.length) {
      var fallbackUrl = normalizeLuckyUrl(link.getAttribute('href'));
      if (fallbackUrl) urls.push(fallbackUrl);
    }

    return urls;
  }

  function primeSearchLuckyLink(target) {
    var link = target && target.closest ? target.closest('[data-search-lucky-link]') : null;
    if (!link) return '';

    var urls = getLuckyUrls(link);
    if (!urls.length) return '';

    var currentUrl = normalizeLuckyUrl(link.getAttribute('href'));
    var candidates = urls.length > 1
      ? urls.filter(function (url) { return url !== currentUrl; })
      : urls;
    var selectedUrl = candidates[Math.floor(Math.random() * candidates.length)] || urls[0];

    link.setAttribute('href', selectedUrl);
    return selectedUrl;
  }

  function initSearchLuckyLinks() {
    var handler = function (event) {
      primeSearchLuckyLink(event.target);
    };

    document.addEventListener('pointerover', handler, true);
    document.addEventListener('focusin', handler, true);
    document.addEventListener('click', handler, true);
  }

  async function initSearchPageSuggestion() {
    if (!document.body || !document.body.classList.contains(SEARCH_PAGE_CLASS)) return;

    var noResults = document.querySelector('.productgrid--no-results');
    var productItems = document.querySelector('.productgrid--items [data-product-item]');
    if (!noResults || productItems) return;

    var input = document.querySelector('[data-live-search-input]');
    var originalQuery = (input && input.value) || new URLSearchParams(window.location.search).get('q') || '';
    if (!originalQuery.trim()) return;

    var suggestion = await findSuggestion(originalQuery);
    if (!suggestion) return;

    injectSearchPageSuggestion(noResults, suggestion, originalQuery.trim());
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
      return;
    }

    fn();
  }

  window.MrMapleSearchIntelligence = {
    cleanTitle: cleanTitle,
    normalizeText: normalizeText,
    getSearchUrl: getSearchUrl,
    loadIndex: loadIndex,
    findSuggestion: findSuggestion,
    hasProductMatches: hasProductMatches,
    dedupePredictiveResults: dedupePredictiveResults,
    rerankPredictiveResults: rerankPredictiveResults,
    clearPredictiveSuggestion: clearPredictiveSuggestion,
    injectPredictiveSuggestion: injectPredictiveSuggestion,
    initSearchPageSuggestion: initSearchPageSuggestion,
    primeSearchLuckyLink: primeSearchLuckyLink,
  };

  ready(function () {
    initSearchLuckyLinks();
    initSearchPageSuggestion();
  });
})();
